/**
 * 音效。用 WebAudio 而不是 <audio> 元素：后者在移动端同一音频快速重触发会互相打断，
 * 落叉时连点会吞音。
 *
 * iOS/Safari 要求音频上下文在用户手势里解锁，所以首次 pointerdown 时才真正创建。
 */

/**
 * 语义键 → 音频文件名。
 *
 * 注意 cross / uncross 这两个是**故意与源文件名相反**的：落白叉用 unmark_x_2、
 * 取消白叉用 mark_x_2。按 Unity 那边的原始命名对上去听感是反的，网页版以听感为准。
 */
const FILES = {
    boardEnter: 'board_enter_1',
    star: 'mark_cat',
    wrong: 'mark_wrong_1',
    cross: 'unmark_x_2',
    uncross: 'mark_x_2',
};

export class Sfx {
    /**
     * @param {string} baseUrl 音频目录
     * @param {boolean} [enabled]
     */
    constructor(baseUrl, enabled = true) {
        this.baseUrl = baseUrl.replace(/\/$/, '');
        this.enabled = enabled;
        /** @type {AudioContext | null} */
        this._ctx = null;
        /** @type {Map<string, AudioBuffer>} */
        this._buffers = new Map();
        /** @type {Map<string, Promise<void>>} */
        this._loading = new Map();
        this._unlocked = false;
    }

    /**
     * 在首个用户手势里调用，解锁音频上下文并开始预载。
     * 多次调用无副作用。
     */
    unlock() {
        if (this._unlocked) return;
        this._unlocked = true;

        const Ctor = window.AudioContext || window.webkitAudioContext;
        if (!Ctor) return; // 不支持 WebAudio：静默无声，不影响玩
        this._ctx = new Ctor();
        if (this._ctx.state === 'suspended') this._ctx.resume().catch(() => {});

        for (const name of Object.keys(FILES)) this._load(name);
    }

    /** @param {string} name */
    _load(name) {
        if (this._buffers.has(name) || this._loading.has(name)) return this._loading.get(name);
        const file = FILES[name];
        if (!file || !this._ctx) return;

        const p = fetch(`${this.baseUrl}/${file}.m4a`)
            .then(r => (r.ok ? r.arrayBuffer() : Promise.reject(new Error(String(r.status)))))
            .then(buf => this._ctx.decodeAudioData(buf))
            .then(decoded => { this._buffers.set(name, decoded); })
            .catch(() => { /* 单个音效失败不该影响游戏 */ })
            .finally(() => { this._loading.delete(name); });

        this._loading.set(name, p);
        return p;
    }

    /**
     * @param {keyof typeof FILES} name
     * @param {number} [volume] 0..1
     */
    play(name, volume = 1) {
        if (!this.enabled || !this._ctx) return;
        const buf = this._buffers.get(name);
        if (!buf) {
            this._load(name); // 还没载好就先补载，本次不出声
            return;
        }
        if (this._ctx.state === 'suspended') this._ctx.resume().catch(() => {});

        const src = this._ctx.createBufferSource();
        src.buffer = buf;
        const gain = this._ctx.createGain();
        gain.gain.value = volume;
        src.connect(gain).connect(this._ctx.destination);
        src.start(0);
    }

    /** @param {boolean} on */
    setEnabled(on) {
        this.enabled = on;
    }
}
